import { useState } from "react";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { Banknote, Clock, CreditCard, Landmark, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useContent } from "@/lib/content";

export const holdLabel = (iso) =>
  iso ? new Date(iso).toLocaleString("en-US", { timeZone: "America/Nassau", weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";

const Option = ({ icon: Icon, title, testId, children }) => (
  <div className="pay-opt" data-testid={testId}>
    <p className="pay-opt-title"><Icon size={16} /> {title}</p>
    {children}
  </div>
);

const PayPalOption = ({ booking, clientId, onPaid, setError }) => {
  const auth = { booking_id: booking.id, token: booking.public_token };
  return (
    <PayPalScriptProvider options={{ clientId, currency: "USD", intent: "capture", components: "buttons", enableFunding: "card" }}>
      <PayPalButtons
        style={{ layout: "vertical", shape: "rect", color: "gold", label: "pay" }}
        createOrder={async () => {
          setError("");
          try { return (await api.post("/payments/paypal/order", auth)).data.order_id; }
          catch (err) { setError(formatApiError(err)); throw err; }
        }}
        onApprove={async (d) => {
          try {
            const { data } = await api.post("/payments/paypal/capture", { ...auth, order_id: d.orderID });
            toast.success("Deposit received — you're confirmed!");
            onPaid(data.booking);
          } catch (err) { setError(formatApiError(err)); }
        }}
        onError={() => setError("Payment could not be completed. Please try again or choose another option.")}
      />
    </PayPalScriptProvider>
  );
};

export const PayStep = ({ booking, payment, onPaid, onLater }) => {
  const c = useContent().data;
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const later = async () => { setBusy(true); try { await onLater(); } catch (err) { setError(formatApiError(err)); } finally { setBusy(false); } };

  return (
    <div className="form pay-step" data-testid="payment-step">
      <p className="eyebrow"><CreditCard size={12} /> Secure your slot</p>
      <h3 data-testid="payment-deposit-title">Pay your ${booking.deposit.toFixed(2)} deposit</h3>
      <p className="muted-sm">{booking.treatment} · {booking.preferred_date} at {booking.time_label}. 50% of {booking.price}. Charged in USD (equal to BSD).</p>
      {booking.hold_until && (
        <p className="pay-hold" data-testid="payment-hold-note"><Clock size={14} /><span>Your time is held until <strong>{holdLabel(booking.hold_until)}</strong>. After that, it may go to another client who pays first.</span></p>
      )}
      {error && <p className="form-error" data-testid="payment-error">{error}</p>}
      <Option icon={CreditCard} title="PayPal or debit / credit card" testId="pay-option-paypal">
        {payment?.paypal.enabled
          ? <PayPalOption booking={booking} clientId={payment.paypal.client_id} onPaid={onPaid} setError={setError} />
          : <p className="muted-sm" data-testid="pay-paypal-unavailable">Online card &amp; PayPal payments are being set up. Use the link in your email to pay as soon as they're live.</p>}
      </Option>
      <Option icon={Landmark} title="Bahamas bank card" testId="pay-option-local-card">
        <p className="muted-sm">Coming soon — local card payments through our bank.</p>
      </Option>
      <Option icon={Banknote} title="Bank transfer or cash" testId="pay-option-bank">
        <p className="muted-sm">Reply to your confirmation email or call <a href={`tel:${c?.contact.phone_call}`}>{c?.contact.phone_call_label}</a> and we'll arrange it. Deposits are confirmed within 24 hours.</p>
      </Option>
      {onLater && (
        <button type="button" className="btn light" disabled={busy} onClick={later} data-testid="payment-pay-later-button">
          {busy ? <Loader2 size={16} className="spin" /> : "Pay later — hold my time for 24 hours"}
        </button>
      )}
    </div>
  );
};
